import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class MixMatchBundle extends HTMLElement {
  #abortController = new AbortController();
  #selections = new Map();
  #variantCache = new WeakMap();
  #activeStepIndex = 0;
  #isAdding = false;
  #autoAdvanceTimer = 0;

  connectedCallback() {
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.style.setProperty('--bundle-progress-count', String(this.steps.length || 1));
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('change', this.#handleChange, { signal });
    this.addEventListener('input', this.#handleInput, { signal });
    this.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });
    this.addEventListener('commerce:block-visibility-change', this.#handleCommerceBlockVisibility, { signal });

    this.productCards.forEach((card, index) => {
      card.dataset.originalIndex = String(index);
      this.#applyInitialVariant(card);
    });

    this.#setActiveStep(0, false);
    this.#render();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    window.clearTimeout(this.#autoAdvanceTimer);
  }

  get steps() {
    return Array.from(this.querySelectorAll('[data-bundle-step]')).filter((element) => !element.classList.contains('commerce-rules-hidden'));
  }

  get productCards() {
    return Array.from(this.querySelectorAll('[data-bundle-product-card]'));
  }

  get progressTriggers() {
    return Array.from(this.querySelectorAll('[data-step-trigger]')).filter((element) => !element.classList.contains('commerce-rules-hidden'));
  }

  get addButton() {
    return this.querySelector('[data-add-bundle]');
  }


  #handleCommerceBlockVisibility = () => {
    this.style.setProperty('--bundle-progress-count', String(this.steps.length || 1));
    if (this.#activeStepIndex >= this.steps.length) this.#activeStepIndex = Math.max(0, this.steps.length - 1);
    this.#setActiveStep(this.#activeStepIndex, false);
    this.#render();
  };

  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const stepTrigger = target.closest('[data-step-trigger]');
    if (stepTrigger instanceof HTMLButtonElement) {
      const index = Number(stepTrigger.dataset.stepIndex || 0);
      if (this.dataset.allowStepNavigation === 'true' || index === this.#activeStepIndex) {
        this.#navigateToStep(index);
      }
      return;
    }

    const nextButton = target.closest('[data-next-step]');
    if (nextButton) {
      this.#navigateToStep(this.#activeStepIndex + 1, true);
      return;
    }

    const previousButton = target.closest('[data-previous-step]');
    if (previousButton) {
      this.#navigateToStep(this.#activeStepIndex - 1);
      return;
    }

    const removeButton = target.closest('[data-summary-remove]');
    if (removeButton) {
      const item = removeButton.closest('[data-summary-item]');
      const cardId = item?.dataset.cardId;
      const card = cardId ? this.querySelector(`#${CSS.escape(cardId)}`) : null;
      if (card instanceof HTMLElement) this.#deselectCard(card);
      return;
    }

    const quantityMinus = target.closest('[data-quantity-minus]');
    if (quantityMinus) {
      const card = quantityMinus.closest('[data-bundle-product-card]');
      if (card instanceof HTMLElement) this.#changeQuantity(card, -1);
      return;
    }

    const quantityPlus = target.closest('[data-quantity-plus]');
    if (quantityPlus) {
      const card = quantityPlus.closest('[data-bundle-product-card]');
      if (card instanceof HTMLElement) this.#changeQuantity(card, 1);
      return;
    }

    const selectButton = target.closest('[data-select-product]');
    if (selectButton) {
      const card = selectButton.closest('[data-bundle-product-card]');
      if (card instanceof HTMLElement) this.#toggleCard(card);
      return;
    }

    if (target.closest('[data-reset-bundle]')) {
      this.#reset();
      return;
    }

    if (target.closest('[data-add-bundle]')) {
      this.#addBundle(event);
    }
  };

  #handleChange = (event) => {
    const target = event.target;

    if (target instanceof HTMLSelectElement && target.matches('[data-step-sort]')) {
      const step = target.closest('[data-bundle-step]');
      if (step instanceof HTMLElement) this.#sortStep(step, target.value);
      return;
    }

    if (
      (target instanceof HTMLSelectElement || target instanceof HTMLInputElement) &&
      target.matches('[data-variant-option]')
    ) {
      const card = target.closest('[data-bundle-product-card]');
      if (card instanceof HTMLElement) this.#updateSelectedVariant(card);
      return;
    }

    if (target instanceof HTMLInputElement && target.matches('[data-quantity-input]')) {
      const card = target.closest('[data-bundle-product-card]');
      if (card instanceof HTMLElement) this.#setQuantity(card, Number(target.value));
    }
  };

  #handleInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.matches('[data-step-search]')) {
      const step = target.closest('[data-bundle-step]');
      if (step instanceof HTMLElement) this.#filterStep(step, target.value);
    }
  };


  #handleBlockSelect = (event) => {
    const step = event.target instanceof Element ? event.target.closest('[data-bundle-step]') : null;
    if (!step) return;
    const index = Number(step.dataset.stepIndex || 0);
    this.#setActiveStep(index, true);
  };

  #applyInitialVariant(card) {
    const variants = this.#getVariants(card);
    if (!variants.length) return;

    const defaultId = Number(card.dataset.defaultVariantId || 0);
    const variant = variants.find((item) => Number(item.id) === defaultId) || variants.find((item) => item.available) || variants[0];
    this.#applyVariant(card, variant, false);
  }

  #getVariants(card) {
    const cached = this.#variantCache.get(card);
    if (cached) return cached;

    const script = card.querySelector('[data-bundle-variants]');
    if (!script) return [];

    try {
      const variants = JSON.parse(script.textContent || '[]');
      this.#variantCache.set(card, variants);
      return variants;
    } catch (error) {
      console.error('[Mix & Match Bundle] Invalid variant JSON', error);
      return [];
    }
  }

  #updateSelectedVariant(card) {
    const variants = this.#getVariants(card);
    if (!variants.length) return;

    const groups = Array.from(card.querySelectorAll('[data-option-position]')).sort(
      (a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)
    );

    const options = groups.map((group) => {
      const select = group.querySelector('select[data-variant-option]');
      if (select instanceof HTMLSelectElement) return select.value;
      const checked = group.querySelector('input[data-variant-option]:checked');
      return checked instanceof HTMLInputElement ? checked.value : '';
    });

    const variant = variants.find(
      (item) => item.options.length === options.length && item.options.every((option, index) => option === options[index])
    );

    this.#applyVariant(card, variant, true);
  }

  #applyVariant(card, variant, preserveSelection) {
    const wasSelected = this.#selections.has(card);
    const isAvailable = Boolean(variant?.available);

    card.dataset.variantId = variant?.id ? String(variant.id) : '';
    card.dataset.variantTitle = variant?.title || '';
    card.dataset.price = String(variant?.price || 0);
    card.dataset.comparePrice = String(variant?.compareAtPrice || 0);
    card.dataset.variantAvailable = String(isAvailable);
    card.dataset.variantImage = variant?.featuredImage || variant?.image || '';
    const configuredMaximum = Math.max(1, Number(card.dataset.configMaxQuantity || card.dataset.maxQuantity || 1));
    const inventoryIsLimited = Boolean(variant?.inventoryManagement) && variant?.inventoryPolicy !== 'continue';
    const availableInventory = Math.max(0, Number(variant?.inventoryQuantity || 0));
    const effectiveMaximum = inventoryIsLimited ? Math.max(1, Math.min(configuredMaximum, availableInventory)) : configuredMaximum;
    card.dataset.maxQuantity = String(effectiveMaximum);
    card.classList.toggle('is-unavailable', !isAvailable);

    const quantityInput = card.querySelector('[data-quantity-input]');
    if (quantityInput instanceof HTMLInputElement) quantityInput.max = String(effectiveMaximum);

    const currentPrice = card.querySelector('[data-current-price]');
    const comparePrice = card.querySelector('[data-compare-price]');
    if (currentPrice) currentPrice.textContent = this.#formatMoney(Number(variant?.price || 0));

    const showCompare = Number(variant?.compareAtPrice || 0) > Number(variant?.price || 0);
    if (comparePrice instanceof HTMLElement) {
      comparePrice.hidden = !showCompare;
      comparePrice.textContent = showCompare ? this.#formatMoney(Number(variant.compareAtPrice)) : '';
    }

    const image = card.querySelector('.mix-match-bundle__product-image');
    if (image instanceof HTMLImageElement && variant?.image) {
      image.src = variant.image;
      image.removeAttribute('srcset');
    }

    const status = card.querySelector('[data-variant-status]');
    if (status) status.textContent = isAvailable ? '' : this.#soldOutLabel(card);

    card.querySelectorAll('[data-select-product]').forEach((button) => {
      if (button instanceof HTMLButtonElement) button.disabled = !isAvailable;
    });

    if (wasSelected && preserveSelection) {
      if (!isAvailable || !variant) {
        this.#deselectCard(card, false);
        this.#setStatus(this.#soldOutLabel(card), false);
      } else {
        const selection = this.#selections.get(card);
        selection.variant = variant;
        selection.quantity = Math.min(selection.quantity, effectiveMaximum);
        this.#selections.set(card, selection);
        if (quantityInput instanceof HTMLInputElement) quantityInput.value = String(selection.quantity);
        this.#render();
      }
    }
  }

  #soldOutLabel(card) {
    return card.dataset.soldOutLabel || card.querySelector('[data-variant-status]')?.textContent?.trim() || 'Sold out';
  }

  #toggleCard(card) {
    if (this.#isAdding || card.dataset.variantAvailable !== 'true') return;

    if (this.#selections.has(card)) {
      this.#deselectCard(card);
    } else {
      this.#selectCard(card);
    }
  }

  #selectCard(card) {
    const step = card.closest('[data-bundle-step]');
    if (!(step instanceof HTMLElement)) return;

    const variant = this.#getCurrentVariant(card);
    if (!variant?.available) return;

    const rule = step.dataset.selectionRule || 'exact';
    const required = Number(step.dataset.requiredCount || 1);
    const currentCount = this.#stepSelectedCount(step);

    if ((rule === 'exact' || rule === 'up_to') && currentCount >= required) {
      this.#setValidation(this.#overLimitMessage(step));
      this.#pulseStep(step);
      return;
    }

    this.#selections.set(card, { card, variant, quantity: 1, step });
    this.#setCardSelectedState(card, true);
    this.#setValidation('');
    this.#render();

    if (this.dataset.autoAdvance === 'true' && this.dataset.layout === 'tabs' && this.#isStepComplete(step)) {
      window.clearTimeout(this.#autoAdvanceTimer);
      this.#autoAdvanceTimer = window.setTimeout(() => {
        const index = Number(step.dataset.stepIndex || 0);
        if (index < this.steps.length - 1) this.#setActiveStep(index + 1, true);
      }, 280);
    }
  }

  #deselectCard(card, shouldRender = true) {
    this.#selections.delete(card);
    this.#setCardSelectedState(card, false);
    if (shouldRender) {
      this.#setValidation('');
      this.#render();
    }
  }

  #setCardSelectedState(card, selected) {
    card.classList.toggle('is-selected', selected);
    card.querySelectorAll('[data-select-product]').forEach((button) => {
      button.setAttribute('aria-pressed', String(selected));
    });

    const selectLabel = card.querySelector('[data-select-label]');
    const selectedLabel = card.querySelector('[data-selected-label]');
    if (selectLabel instanceof HTMLElement) selectLabel.hidden = selected;
    if (selectedLabel instanceof HTMLElement) selectedLabel.hidden = !selected;

    const quantity = card.querySelector('[data-card-quantity]');
    if (quantity instanceof HTMLElement) quantity.hidden = !selected;
    if (!selected) {
      const input = card.querySelector('[data-quantity-input]');
      if (input instanceof HTMLInputElement) input.value = '1';
    }
  }

  #getCurrentVariant(card) {
    const variants = this.#getVariants(card);
    const variantId = Number(card.dataset.variantId || card.dataset.defaultVariantId || 0);
    return variants.find((variant) => Number(variant.id) === variantId) || null;
  }

  #changeQuantity(card, delta) {
    const selection = this.#selections.get(card);
    if (!selection) return;
    this.#setQuantity(card, selection.quantity + delta);
  }

  #setQuantity(card, requestedQuantity) {
    const selection = this.#selections.get(card);
    if (!selection) return;

    const maxPerProduct = Math.max(1, Number(card.dataset.maxQuantity || 1));
    let quantity = Math.max(1, Math.min(maxPerProduct, Number.isFinite(requestedQuantity) ? requestedQuantity : 1));
    const step = selection.step;
    const rule = step.dataset.selectionRule || 'exact';
    const required = Number(step.dataset.requiredCount || 1);
    const otherQuantity = this.#stepSelectedCount(step) - selection.quantity;

    if ((rule === 'exact' || rule === 'up_to') && otherQuantity + quantity > required) {
      quantity = Math.max(1, required - otherQuantity);
      this.#setValidation(this.#overLimitMessage(step));
      this.#pulseStep(step);
    } else {
      this.#setValidation('');
    }

    selection.quantity = quantity;
    this.#selections.set(card, selection);

    const input = card.querySelector('[data-quantity-input]');
    if (input instanceof HTMLInputElement) input.value = String(quantity);
    this.#render();
  }

  #stepSelectedCount(step) {
    let count = 0;
    for (const selection of this.#selections.values()) {
      if (selection.step === step) count += selection.quantity;
    }
    return count;
  }

  #isStepComplete(step) {
    const count = this.#stepSelectedCount(step);
    const required = Number(step.dataset.requiredCount || 1);
    const rule = step.dataset.selectionRule || 'exact';

    if (rule === 'at_least') return count >= required;
    if (rule === 'up_to') return count > 0 && count <= required;
    return count === required;
  }

  #allStepsComplete() {
    return this.steps.length > 0 && this.steps.every((step) => this.#isStepComplete(step));
  }

  #render() {
    this.#renderStepStates();
    this.#renderSummary();
    this.#renderTotals();
    this.#renderProgress();
    this.#renderAddButton();
  }

  #renderStepStates() {
    this.steps.forEach((step) => {
      const count = this.#stepSelectedCount(step);
      const counter = step.querySelector('[data-step-selected-count]');
      if (counter) counter.textContent = String(count);
      step.classList.toggle('is-complete', this.#isStepComplete(step));
    });
  }

  #renderSummary() {
    const container = this.querySelector('[data-summary-items]');
    const template = this.querySelector('[data-summary-item-template]');
    const empty = this.querySelector('[data-summary-empty]');
    if (!(container instanceof HTMLElement) || !(template instanceof HTMLTemplateElement)) return;

    container.replaceChildren();
    const selections = Array.from(this.#selections.values()).sort((a, b) => {
      const stepDifference = Number(a.step.dataset.stepIndex || 0) - Number(b.step.dataset.stepIndex || 0);
      if (stepDifference !== 0) return stepDifference;
      return Number(a.card.dataset.originalIndex || 0) - Number(b.card.dataset.originalIndex || 0);
    });

    selections.forEach((selection) => {
      const fragment = template.content.cloneNode(true);
      const item = fragment.querySelector('[data-summary-item]');
      if (!(item instanceof HTMLElement)) return;

      item.dataset.cardId = selection.card.id;
      const title = item.querySelector('[data-summary-title]');
      const variant = item.querySelector('[data-summary-variant]');
      const price = item.querySelector('[data-summary-item-price]');
      const quantity = item.querySelector('[data-summary-quantity]');
      const image = item.querySelector('[data-summary-image]');

      if (title) title.textContent = selection.card.dataset.productTitle || '';
      if (variant) {
        const variantTitle = selection.variant?.title || '';
        variant.textContent = variantTitle === 'Default Title' ? '' : variantTitle;
      }
      if (price) price.textContent = this.#formatMoney(Number(selection.variant.price || 0) * selection.quantity);
      if (quantity) quantity.textContent = `×${selection.quantity}`;
      if (image instanceof HTMLImageElement) {
        const source = selection.variant?.featuredImage || selection.variant?.image || selection.card.dataset.variantImage;
        if (source) {
          image.src = source;
          image.alt = selection.card.dataset.productTitle || '';
        } else {
          image.closest('.mix-match-bundle__summary-image-wrap')?.setAttribute('hidden', '');
        }
      }
      container.appendChild(fragment);
    });

    if (empty instanceof HTMLElement) empty.hidden = selections.length > 0;
  }

  #renderTotals() {
    const subtotal = this.#subtotal();
    const savings = this.#allStepsComplete() ? this.#discountAmount(subtotal) : 0;
    const total = Math.max(0, subtotal - savings);

    const subtotalElement = this.querySelector('[data-subtotal]');
    const savingsElement = this.querySelector('[data-savings]');
    const totalElement = this.querySelector('[data-total]');
    const savingsRow = this.querySelector('[data-saving-row]');

    if (subtotalElement) subtotalElement.textContent = this.#formatMoney(subtotal);
    if (savingsElement) savingsElement.textContent = `−${this.#formatMoney(savings)}`;
    if (totalElement) totalElement.textContent = this.#formatMoney(total);
    if (savingsRow instanceof HTMLElement) savingsRow.hidden = savings <= 0;

    this.#renderShipping(total);
  }

  #subtotal() {
    let total = 0;
    for (const selection of this.#selections.values()) {
      total += Number(selection.variant?.price || 0) * selection.quantity;
    }
    return total;
  }

  #discountAmount(subtotal) {
    if (this.dataset.discountType === 'percentage') {
      return Math.round(subtotal * (Math.max(0, Number(this.dataset.discountPercent || 0)) / 100));
    }
    if (this.dataset.discountType === 'fixed') {
      return Math.min(subtotal, Math.max(0, Number(this.dataset.fixedDiscount || 0)));
    }
    return 0;
  }

  #renderShipping(total) {
    const target = Number(this.dataset.freeShippingTarget || 0);
    const wrapper = this.querySelector('[data-shipping-progress]');
    if (!target || !(wrapper instanceof HTMLElement)) return;

    const message = wrapper.querySelector('[data-shipping-message]');
    const bar = wrapper.querySelector('[data-shipping-bar]');
    const remaining = Math.max(0, target - total);
    const ratio = Math.max(0, Math.min(1, total / target));

    if (bar instanceof HTMLElement) bar.style.transform = `scaleX(${ratio})`;
    if (message) {
      if (remaining <= 0) {
        message.textContent = this.dataset.freeShippingReachedLabel || 'You unlocked free shipping!';
      } else {
        const template = this.dataset.freeShippingRemainingLabel || 'Add [amount] more for free shipping';
        message.textContent = template.replace('[amount]', this.#formatMoney(remaining));
      }
    }
  }

  #renderProgress() {
    const totalRequired = Math.max(1, Number(this.dataset.totalRequired || 1));
    const totalSelected = Array.from(this.#selections.values()).reduce((sum, selection) => sum + selection.quantity, 0);
    const count = this.querySelector('[data-total-selected-count]');
    const summaryProgress = this.querySelector('[data-summary-progress]');
    const overallProgress = this.querySelector('[data-overall-progress-bar]');
    const ratio = Math.max(0, Math.min(1, totalSelected / totalRequired));

    if (count) count.textContent = String(totalSelected);
    if (summaryProgress instanceof HTMLElement) summaryProgress.style.transform = `scaleX(${ratio})`;

    let completedSteps = 0;
    this.steps.forEach((step, index) => {
      const complete = this.#isStepComplete(step);
      if (complete) completedSteps += 1;
      const trigger = this.progressTriggers[index];
      if (trigger) {
        trigger.classList.toggle('is-complete', complete);
        trigger.classList.toggle('is-active', index === this.#activeStepIndex);
        trigger.setAttribute('aria-current', index === this.#activeStepIndex ? 'step' : 'false');
        const progressNavigationDisabled = this.dataset.allowStepNavigation !== 'true' && index !== this.#activeStepIndex;
        trigger.disabled = progressNavigationDisabled || !this.#canNavigateTo(index);
      }
    });

    const progressRatio = this.steps.length <= 1 ? (completedSteps ? 1 : 0) : completedSteps / this.steps.length;
    if (overallProgress instanceof HTMLElement) overallProgress.style.transform = `scaleX(${progressRatio})`;
  }

  #renderAddButton() {
    const button = this.addButton;
    if (!(button instanceof HTMLButtonElement)) return;
    button.disabled = this.#isAdding || !this.#allStepsComplete();
  }

  #navigateToStep(index, validateCurrent = false) {
    if (this.dataset.layout !== 'tabs') {
      const step = this.steps[Math.max(0, Math.min(index, this.steps.length - 1))];
      step?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    if (validateCurrent) {
      const current = this.steps[this.#activeStepIndex];
      if (current && !this.#isStepComplete(current)) {
        this.#setValidation(this.#incompleteMessage(current));
        this.#pulseStep(current);
        return;
      }
    }

    if (!this.#canNavigateTo(index)) {
      const blocker = this.steps.slice(0, index).find((step) => !this.#isStepComplete(step));
      if (blocker) {
        this.#setValidation(this.#incompleteMessage(blocker));
        this.#pulseStep(blocker);
      }
      return;
    }

    this.#setValidation('');
    this.#setActiveStep(index, true);
  }

  #canNavigateTo(index) {
    if (index < 0 || index >= this.steps.length) return false;
    if (this.dataset.requireStepOrder !== 'true' || index <= this.#activeStepIndex) return true;
    return this.steps.slice(0, index).every((step) => this.#isStepComplete(step));
  }

  #setActiveStep(index, scroll) {
    if (!this.steps.length) return;
    const bounded = Math.max(0, Math.min(index, this.steps.length - 1));
    this.#activeStepIndex = bounded;

    this.steps.forEach((step, stepIndex) => {
      const active = stepIndex === bounded;
      step.classList.toggle('is-active', active);
      if (this.dataset.layout === 'tabs') step.hidden = !active;
    });

    this.progressTriggers.forEach((trigger, triggerIndex) => {
      trigger.classList.toggle('is-active', triggerIndex === bounded);
      trigger.setAttribute('aria-current', triggerIndex === bounded ? 'step' : 'false');
    });

    if (scroll) {
      const step = this.steps[bounded];
      step?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      const firstInteractive = step?.querySelector('[data-step-search], [data-select-product]:not(:disabled)');
      window.setTimeout(() => firstInteractive?.focus({ preventScroll: true }), 260);
    }

    this.#renderProgress();
  }

  #filterStep(step, searchTerm) {
    const normalized = searchTerm.trim().toLocaleLowerCase();
    let visible = 0;

    step.querySelectorAll('[data-bundle-product-card]').forEach((card) => {
      const haystack = `${card.dataset.productTitle || ''} ${card.dataset.productVendor || ''}`.toLocaleLowerCase();
      const matches = !normalized || haystack.includes(normalized);
      card.hidden = !matches;
      if (matches) visible += 1;
    });

    const empty = step.querySelector('[data-empty-search]');
    if (empty instanceof HTMLElement) empty.hidden = visible > 0;
  }

  #sortStep(step, sortValue) {
    const container = step.querySelector('[data-step-products]');
    if (!(container instanceof HTMLElement)) return;

    const cards = Array.from(container.querySelectorAll('[data-bundle-product-card]'));
    cards.sort((a, b) => {
      if (sortValue === 'price-asc') return Number(a.dataset.price || 0) - Number(b.dataset.price || 0);
      if (sortValue === 'price-desc') return Number(b.dataset.price || 0) - Number(a.dataset.price || 0);
      if (sortValue === 'title-asc') return (a.dataset.productTitle || '').localeCompare(b.dataset.productTitle || '');
      return Number(a.dataset.originalIndex || 0) - Number(b.dataset.originalIndex || 0);
    });

    cards.forEach((card) => container.appendChild(card));
  }

  #incompleteMessage(step) {
    const count = this.#stepSelectedCount(step);
    const required = Number(step.dataset.requiredCount || 1);
    const remaining = Math.max(1, required - count);
    const title = step.querySelector('h3')?.textContent?.trim() || 'this step';
    return (this.dataset.stepIncompleteLabel || 'Choose [remaining] more item(s) in [step].')
      .replace('[remaining]', String(remaining))
      .replace('[required]', String(required))
      .replace('[step]', title);
  }

  #overLimitMessage(step) {
    const required = Number(step.dataset.requiredCount || 1);
    const title = step.querySelector('h3')?.textContent?.trim() || 'this step';
    return (this.dataset.stepOverLimitLabel || 'You can choose up to [required] item(s) in [step].')
      .replace('[required]', String(required))
      .replace('[step]', title);
  }

  #pulseStep(step) {
    step.animate?.(
      [
        { transform: 'translateX(0)' },
        { transform: 'translateX(-5px)' },
        { transform: 'translateX(5px)' },
        { transform: 'translateX(0)' },
      ],
      { duration: 220, easing: 'ease-out' }
    );
  }

  #setValidation(message) {
    const element = this.querySelector('[data-validation-message]');
    if (element) element.textContent = message;
  }

  #setStatus(message, success = false) {
    const element = this.querySelector('[data-bundle-status]');
    if (!element) return;
    element.textContent = message;
    element.classList.toggle('is-success', success);
  }

  #reset() {
    if (this.#isAdding) return;
    this.#selections.forEach((_, card) => this.#setCardSelectedState(card, false));
    this.#selections.clear();
    this.#setValidation('');
    this.#setStatus('');
    this.#setActiveStep(0, true);
    this.#render();
  }

  async #addBundle(event) {
    if (this.#isAdding) return;

    const incomplete = this.steps.find((step) => !this.#isStepComplete(step));
    if (incomplete) {
      this.#setValidation(this.#incompleteMessage(incomplete));
      this.#setActiveStep(Number(incomplete.dataset.stepIndex || 0), true);
      this.#pulseStep(incomplete);
      return;
    }

    const expectedDiscount = this.#discountAmount(this.#subtotal());
    const discountMode = this.dataset.discountApplication || 'code';
    const discountCode = String(this.dataset.discountCode || '').trim();
    const realDiscountRequired = this.dataset.requireRealDiscount !== 'false';
    const engine = window.BundleDiscountEngine;

    if (expectedDiscount > 0 && discountMode === 'code' && !discountCode) {
      this.#setValidation(
        this.dataset.discountSetupErrorLabel || 'This offer is not configured yet. Please contact the store owner.'
      );
      return;
    }

    if (expectedDiscount > 0 && !engine) {
      this.#setStatus(this.dataset.discountErrorLabel || 'The discount could not be applied.', false);
      return;
    }

    const bundleId = `mix-${this.dataset.sectionId || 'bundle'}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const items = this.#buildCartItems(bundleId, expectedDiscount > 0);
    if (!items.length) return;

    this.#setAddingState(true);
    this.#setValidation('');
    this.#setStatus('');

    const cartSectionIds = this.#cartSectionIds();
    const deferredPromise = CartLinesUpdateEvent.createPromise();
    const lines = items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity }));

    const shouldAutoOpenCart =
      this.dataset.postAddAction === 'stay' && this.dataset.openCartAfterAdd === 'true';

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: shouldAutoOpenCart ? 'add' : 'update',
        context: 'product',
        lines,
        promise: deferredPromise.promise,
      })
    );

    let finalSections;
    let beforeCart = null;

    try {
      if (expectedDiscount > 0 && discountMode === 'automatic') {
        beforeCart = await engine.getCart();
      }

      const addUrl = window.Theme?.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`;
      const payload = { items };
      if (cartSectionIds.length) payload.sections = cartSectionIds.join(',');
      payload.sections_url = window.location.pathname + window.location.search;

      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      });

      const addResponse = await response.json();
      if (!response.ok || addResponse.status) {
        throw new Error(addResponse.description || addResponse.message || 'Add to cart failed');
      }
      finalSections = addResponse.sections;

      let cart = null;
      let realDiscountApplied = expectedDiscount <= 0;

      if (expectedDiscount > 0 && discountMode === 'code') {
        const discountResult = await engine.applyCode(discountCode, cartSectionIds);
        cart = discountResult.cart;
        realDiscountApplied = discountResult.applied;
        if (cart?.sections) finalSections = cart.sections;
      } else if (expectedDiscount > 0 && discountMode === 'automatic') {
        cart = await engine.getCart();
        realDiscountApplied = engine.automaticApplied(
          beforeCart,
          cart,
          bundleId,
          this.dataset.automaticDiscountTitle || ''
        );
      }

      if (!realDiscountApplied && realDiscountRequired) {
        cart = await engine.rollback(bundleId, cartSectionIds);
        if (cart?.sections) finalSections = cart.sections;

        deferredPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'mix-match-bundle',
            sourceId: this.id,
            itemCount: 0,
            sections: finalSections,
            didError: true,
          },
        });

        this.#setStatus(
          this.dataset.discountErrorLabel || 'The discount could not be applied, so the bundle was not left in your cart.',
          false
        );
        return;
      }

      if (!cart) cart = await this.#getCart();

      deferredPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'mix-match-bundle',
          sourceId: this.id,
          itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
          sections: finalSections,
          didError: false,
        },
      });

      if (!realDiscountApplied && expectedDiscount > 0) {
        this.#setStatus(this.dataset.discountErrorLabel || 'The discount could not be applied.', false);
      } else {
        this.#setStatus(this.dataset.bundleAddedLabel || 'Your bundle was added to the cart.', true);
      }

      await this.#afterSuccessfulAdd();
      if (this.dataset.resetAfterAdd === 'true' && this.dataset.postAddAction === 'stay') this.#resetAfterSuccess();
    } catch (error) {
      console.error('[Mix & Match Bundle]', error);

      if (expectedDiscount > 0 && realDiscountRequired && engine) {
        try {
          const cart = await engine.rollback(bundleId, cartSectionIds);
          deferredPromise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: {
              items: cart.items,
              source: 'mix-match-bundle',
              sourceId: this.id,
              itemCount: 0,
              sections: cart.sections,
              didError: true,
            },
          });
        } catch (rollbackError) {
          console.error('[Mix & Match Bundle] Rollback failed', rollbackError);
          deferredPromise.reject(error);
        }
      } else {
        deferredPromise.reject(error);
      }

      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Unable to add bundle',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#setStatus(
        expectedDiscount > 0
          ? (this.dataset.discountErrorLabel || 'The discount could not be applied, so the bundle was not left in your cart.')
          : (this.dataset.bundleErrorLabel || 'We couldn’t add the bundle. Please try again.'),
        false
      );
    } finally {
      this.#setAddingState(false);
      if (event instanceof Event && window.cartPerformance?.measureFromEvent) {
        window.cartPerformance.measureFromEvent('add:user-action', event);
      }
    }
  }

  #buildCartItems(bundleId, forceProperties = false) {
    const addProperties = this.dataset.addProperties === 'true' || forceProperties;
    const items = [];

    for (const selection of this.#selections.values()) {
      const item = {
        id: Number(selection.variant.id),
        quantity: selection.quantity,
      };

      if (addProperties) {
        item.properties = {
          _bundle_instance_id: bundleId,
          _bundle_id: bundleId,
          _bundle_type: 'mix-match',
          _bundle_name: this.dataset.bundleName || 'Mix & Match Bundle',
          _bundle_step: selection.step.querySelector('h3')?.textContent?.trim() || '',
        };
        if (this.dataset.discountCode) item.properties._bundle_discount_code = this.dataset.discountCode;
      }

      items.push(item);
    }

    if (!addProperties) {
      const merged = new Map();
      items.forEach((item) => {
        const current = merged.get(item.id) || { ...item, quantity: 0 };
        current.quantity += item.quantity;
        merged.set(item.id, current);
      });
      return Array.from(merged.values());
    }

    return items;
  }

  #cartSectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId && !ids.includes(component.dataset.sectionId)) {
        ids.push(component.dataset.sectionId);
      }
    });
    return ids;
  }

  async #getCart() {
    const cartUrl = window.Theme?.routes?.cart_url
      ? `${window.Theme.routes.cart_url}.js`
      : `${window.Shopify?.routes?.root || '/'}cart.js`;
    const response = await fetch(cartUrl, { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Unable to refresh cart');
    return response.json();
  }

  async #afterSuccessfulAdd() {
    const action = this.dataset.postAddAction || 'stay';
    const root = window.Shopify?.routes?.root || '/';

    if (action === 'cart') {
      window.location.assign(`${root}cart`);
      return;
    }

    if (action === 'checkout') {
      window.location.assign(`${root}checkout`);
      return;
    }

    if (window.ThemeCartSync?.sync) {
      try {
        await window.ThemeCartSync.sync({ animateCount: true });
      } catch (error) {
        console.warn('[Mix & Match Bundle] Cart sync fallback', error);
      }
    } else {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      await new Promise((resolve) => window.setTimeout(resolve, 80));
    }

    if (this.dataset.openCartAfterAdd === 'true') {
      if (window.Shopify?.actions?.openCart) {
        try {
          await window.Shopify.actions.openCart();
          return;
        } catch (error) {
          console.warn('[Mix & Match Bundle] Standard openCart action failed', error);
        }
      }

      const drawer = document.querySelector('cart-drawer-component, cart-drawer');
      if (drawer?.open) drawer.open();
      else document.querySelector('[data-cart-drawer-trigger], .header-actions__cart-icon')?.click();
    }
  }

  #resetAfterSuccess() {
    window.setTimeout(() => {
      this.#selections.forEach((_, card) => this.#setCardSelectedState(card, false));
      this.#selections.clear();
      this.#setActiveStep(0, false);
      this.#render();
    }, 250);
  }

  #setAddingState(adding) {
    this.#isAdding = adding;
    const button = this.addButton;
    const normal = this.querySelector('[data-add-button-label]');
    const loading = this.querySelector('[data-add-loading-label]');
    if (button instanceof HTMLButtonElement) {
      button.setAttribute('aria-busy', String(adding));
      button.disabled = adding || !this.#allStepsComplete();
    }
    if (normal instanceof HTMLElement) normal.hidden = adding;
    if (loading instanceof HTMLElement) loading.hidden = !adding;
  }

  #formatMoney(value) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || 'USD';
    try {
      return formatMoney(Number(value || 0), format, currency);
    } catch (error) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
      }).format(Number(value || 0) / 100);
    }
  }
}

if (!customElements.get('mix-match-bundle')) {
  customElements.define('mix-match-bundle', MixMatchBundle);
}
